import toast from 'react-hot-toast';
import {
  useCreateFileMappings,
  useSaveAutomationMappings
} from '../../../../services/automation.service';
import { useDeletePeriodFile } from '../../../../services/period.service';
import {
  serviceFundMapping,
  useReUploadFundFile,
  useUploadFundFiles
} from '../../../../services/funds.service';

import { useCallback, useState } from 'react';
import { useDeleteSheet } from '../../../../services/workflow.services';
import { cloneDeep } from 'lodash';
import { extractApiError } from '../../../../utils';
import { validateUploadFiles } from '../utils/uploadValidation';

export default function useMutateAutomationFiles({
  fundMappings,
  refetch,
  periodId,
  hasChanges,
  openPopup,
  onSaveSuccess
}) {
  const [isOpenWarning, setIsOpenWarning] = useState(false);

  const saveMappings = useSaveAutomationMappings();
  const deletePeriodFile = useDeletePeriodFile();
  const deletePeriodSheet = useDeleteSheet();
  const uploadFundFiles = useUploadFundFiles();
  const reUploadFundFile = useReUploadFundFile();
  const createFileMappings = useCreateFileMappings({
    periodId
  });

  const unSupportedFileToastMessage =
    'Unsupported file format. Please upload an Excel or CSV file.';

  const isUnsupportedFileTypeError = err => {
    const detail = err?.data?.detail;
    return (
      err?.status === 415 && typeof detail === 'string' && detail.includes('Unsupported Media Type')
    );
  };

  const handlePreventReset = callback => {
    return async (...args) => {
      if (hasChanges) {
        const userResponse = await openPopup();
        if (!userResponse) return;
      }

      callback(...args);
    };
  };

  const handleDeletePeriodFile = (fileId, fn) => {
    deletePeriodFile.mutate(
      { periodId, fileId },
      {
        onSuccess: () => {
          refetch();
          fn?.();
        },
        onError: err => {
          toast.error('Error on deleting file');
        }
      }
    );
  };

  const handleDownloadFile = async (fileId, fileName, customPeriodId) => {
    toast.promise(
      serviceFundMapping.downloadFile({ fileId, periodId: customPeriodId || periodId }),
      {
        loading: 'Downloading...',
        success: response => {
          const link = document.createElement('a');
          link.href = URL.createObjectURL(response);

          link.download = fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          URL.revokeObjectURL(link.href);

          return 'Downloaded';
        },
        error: err => {
          console.log(err);
          return 'Error on downloading file';
        }
      }
    );
  };

  const handleDeletePeriodSheet = sheetId => {
    toast.promise(deletePeriodSheet.mutateAsync(sheetId), {
      loading: 'Deleting...',
      success: () => {
        refetch();

        return 'Deleted';
      },
      error: 'Error on deleting sheet'
    });
  };

  const onChooseFile = (currentFileId, previousFileId) => {
    createFileMappings.mutate(
      [
        {
          previous_period_file_id: previousFileId,
          current_period_file_id: currentFileId
        }
      ],
      {
        onSuccess: () => {
          refetch();
        },
        onError: () => {
          refetch();
        }
      }
    );
  };

  const handleUploadFundFiles = (e, fundId) => {
    const { files } = e.target;
    const validationError = validateUploadFiles(files);

    if (validationError) {
      toast.error(validationError);
      e.target.value = '';
      return;
    }

    const formData = new FormData();

    [...files].forEach(file => formData.append('files', file));

    toast.promise(uploadFundFiles.mutateAsync({ data: formData, periodId, fundId }), {
      loading: 'Uploading...',
      success: res => {
        refetch();

        return 'Uploaded';
      },
      error: err => {
        refetch();

        if (isUnsupportedFileTypeError(err)) {
          return unSupportedFileToastMessage;
        }

        return extractApiError(err, 'Error on uploading files');
      }
    });
    e.target.value = '';
  };

  const handleReUploadFundFile = (e, fileId) => {
    const { files } = e.target;
    const validationError = validateUploadFiles(files);

    if (validationError) {
      toast.error(validationError);
      e.target.value = '';
      return;
    }

    const formData = new FormData();

    [...files].forEach(file => formData.append('file', file));

    toast.promise(reUploadFundFile.mutateAsync({ data: formData, periodId, fileId }), {
      loading: 'Uploading...',
      success: res => {
        refetch();

        return 'Uploaded';
      },
      error: err => {
        refetch();

        const detail = err?.data?.detail;

        if (isUnsupportedFileTypeError(err)) {
          return unSupportedFileToastMessage;
        }

        return extractApiError(err, 'Error on uploading file');
      }
    });
    e.target.value = '';
  };

  const handleDeleteFile = (fileId, fn) => {
    return handleDeletePeriodFile(fileId, fn);
  };

  const onUploadFilesForAllFunds = e => {
    const { files } = e.target;
    const validationError = validateUploadFiles(files);

    if (validationError) {
      toast.error(validationError, { position: 'top-center' });
      e.target.value = '';
      return;
    }

    const totalFiles = files.length;
    const formData = new FormData();

    [...files].forEach(file => formData.append('files', file));

    let uploadedCount = 0;

    // Create a toast with an ID so it can be updated dynamically
    const toastId = toast.loading(`Uploading ${uploadedCount}/${totalFiles}`, {
      style: {
        width: 200
      },
      //   icon: LoadingCircle,
      position: 'top-center'
    });

    uploadFundFiles.mutate(
      {
        data: formData,
        periodId,
        onUploadProgress: data => {
          let cumulativeSize = 0;

          // Calculate uploaded file count based on cumulative size
          [...files].forEach((file, idx) => {
            cumulativeSize += file.size;
            if (data.loaded >= cumulativeSize) {
              uploadedCount = idx + 1; // Update uploaded count
            }
          });

          // Update toast with current progress
          toast.loading(`Uploading ${Math.min(uploadedCount, totalFiles)}/${totalFiles}`, {
            id: toastId
          });
        }
      },
      {
        onSuccess: () => {
          toast.success('All files uploaded successfully!', {
            position: 'top-center'
          });
        },
        onError: err => {
          const message = isUnsupportedFileTypeError(err)
            ? unSupportedFileToastMessage
            : extractApiError(err, 'Failed to upload files.');

          toast.error(message, { position: 'top-center' });
        },
        onSettled: () => {
          refetch();
          toast.dismiss(toastId);
          e.target.value = '';
        }
      }
    );
  };

  const hasUnifinishedMappings = useCallback(() => {
    let isFinished = true;

    fundMappings.forEach(mapping => {
      mapping.bulkFiles.forEach(fileMapping => {
        // current file (multi/single) not uploaded
        if (!fileMapping.currentFile && fileMapping.previousFile) {
          isFinished = false;

          return;
        }

        // current file (multi/single) not mapped to previous
        if (!fileMapping.previousFile && fileMapping.currentFile) {
          isFinished = false;

          return;
        }

        // current sheet (multi/single) not mapped to previous
        if (
          fileMapping.currentFile.sheets.filter(el => el.id).length !==
          fileMapping.previousFile.sheets.filter(el => el.id).length
        ) {
          isFinished = false;

          return;
        }

        // knowledge not selected for new current sheet (multi/single)
        if (
          !fileMapping.previousFile &&
          fileMapping.currentFile &&
          fileMapping.currentFile.sheets.find(el => !el.source.id)
        ) {
          isFinished = false;

          return;
        }
      });

      mapping.perFundFiles.forEach(fileMapping => {
        // current file (multi/single) not uploaded
        if (!fileMapping.currentFile && fileMapping.previousFile) {
          isFinished = false;

          return;
        }

        // current file (multi/single) not mapped to previous
        if (!fileMapping.previousFile && fileMapping.currentFile) {
          isFinished = false;

          return;
        }

        // current sheet (multi/single) not mapped to previous
        if (
          fileMapping.currentFile.sheets.filter(el => el.id).length !==
          fileMapping.previousFile.sheets.filter(el => el.id).length
        ) {
          isFinished = false;

          return;
        }

        // knowledge not selected for new current sheet (multi/single)
        if (
          !fileMapping.previousFile &&
          fileMapping.currentFile &&
          fileMapping.currentFile.sheets.find(el => !el.source.id)
        ) {
          isFinished = false;

          return;
        }
      });
    });

    return !isFinished;
  }, [JSON.stringify(fundMappings)]);

  const onSave = useCallback(() => {
    // check to unfinished mappings first & let user to know about it
    if (hasUnifinishedMappings() && !isOpenWarning) {
      return setIsOpenWarning(true);
    }

    const allSheetsWithManualExtractions = [];
    const allSheetsWithoutPreviousFiles = [];
    const allSheetsWithPreviousFiles = [];

    function filterUniqueArray(data) {
      const seen = new Set(); // Set to track unique entries
      const result = []; // Result array to store filtered entries

      data.forEach(item => {
        const uniqueKey = `${item?.previous_period_file_id}_${item?.current_period_file_id}`;

        if (!seen.has(uniqueKey)) {
          seen.add(uniqueKey); // Mark this combination as seen
          result.push(item); // Add to the result
        }
      });

      return result;
    }

    function filterUniqueArraySheets(data) {
      const seen = new Set(); // Set to track unique entries
      const result = []; // Result array to store filtered entries

      data.forEach(item => {
        const uniqueKey = item?.current_period_sheet_id;

        if (!seen.has(uniqueKey)) {
          seen.add(uniqueKey); // Mark this combination as seen
          result.push(item); // Add to the result
        }
      });

      return result;
    }
    const _fundMappings = cloneDeep(fundMappings);

    _fundMappings.forEach(mapping => {
      const filteredBulkFiles = cloneDeep(mapping.bulkFiles.filter(file => file.currentFile));
      const filteredPerFundFiles = cloneDeep(mapping.perFundFiles.filter(file => file.currentFile));

      const mergedFiles = [...filteredBulkFiles, ...filteredPerFundFiles];

      mergedFiles.forEach(file => {
        // find sheets with manual entry
        file.currentFile?.sheets?.forEach(sheet => {
          if (sheet.source?.name === 'Manual Entry') {
            allSheetsWithManualExtractions.push(sheet.id);
          }
        });

        if (file.previousFile && JSON.stringify(file.previousFile) !== '{}') {
          allSheetsWithPreviousFiles.push({
            previous_period_file_id: file.previousFile.id,
            current_period_file_id: file.currentFile?.id,
            sheet_mappings: [
              ...filterUniqueArraySheets(
                file.currentFile.sheets
                  .map(sheet => ({
                    previous_period_sheet_id: sheet.source?.id !== 1 && sheet.source?.id,
                    current_period_sheet_id: sheet.id
                  }))
                  .filter(
                    sheet => sheet?.previous_period_sheet_id && sheet?.current_period_sheet_id
                  )
              )
            ]
          });
        } else if (file.currentFile.sheets?.length > 0) {
          file.currentFile.sheets.forEach(sheet => {
            if (sheet.source?.id && sheet.source?.id !== 1 && sheet.id) {
              allSheetsWithoutPreviousFiles.push({
                previous_period_sheet_id: sheet.source?.id,
                current_period_sheet_id: sheet.id
              });
            }
          });
        }
      });
    });

    const fileMappings = filterUniqueArray(allSheetsWithPreviousFiles).filter(
      el => el.previous_period_file_id && el.current_period_file_id
    );

    saveMappings.mutate(
      {
        periodId,
        data: {
          file_mappings: [...fileMappings],
          sheet_mappings_without_file_mapping: [
            ...filterUniqueArraySheets([...allSheetsWithoutPreviousFiles])
          ],
          sheets_for_manual_extraction: [...new Set(allSheetsWithManualExtractions || [])]
        }
      },
      {
        onSuccess: res => {
          refetch();
          setIsOpenWarning(false);

          toast.success('Successfuly saved', {
            position: 'top-center'
          });
          onSaveSuccess?.();
        },
        onError: err => {
          toast.error('Error on saving', {
            position: 'top-center'
          });
        }
      }
    );
  }, [fundMappings, hasUnifinishedMappings, isOpenWarning, onSaveSuccess]);

  return {
    handleDeletePeriodFile: handlePreventReset(handleDeletePeriodSheet),
    onChooseFile: handlePreventReset(onChooseFile),
    handleUploadFundFiles,
    handleDeleteFile: handlePreventReset(handleDeleteFile),
    onUploadFilesForAllFunds,
    onSave,
    handleDeletePeriodSheet: handlePreventReset(handleDeletePeriodSheet),
    handleReUploadFundFile,
    handleDownloadFile,
    isSaveMappingsLoading: saveMappings.isLoading,
    isOpenWarning,
    isDeletingFile: deletePeriodFile.isLoading,
    closeWarning: () => setIsOpenWarning(false),
    openWarning: () => setIsOpenWarning(true),
    handlePreventReset
  };
}
